<template>
  <div class="p-4">
    <el-form
      :model="form"
      :rules="rules"
      :disabled="disabled"
      ref="form"
    >
      <!-- 标题部分 -->
      <div class="mb-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xs font-bold text-gray-900">标题</h3>
          <div class="flex items-center space-x-2">
            <el-button
              type="text"
              size="mini"
              class="text-blue-500"
              @click="loadRecommendedHeadlines"
            >
              <i class="el-icon-magic-stick mr-1"></i>标题推荐
            </el-button>
            <el-button
              type="primary"
              size="mini"
              @click="addHeadline"
              :disabled="form.headlines.length >= 15"
            >
              <i class="el-icon-plus mr-1"></i>添加
            </el-button>
          </div>
        </div>

        <div class="space-y-4">
          <el-form-item
            v-for="(headline, index) in form.headlines"
            :key="index"
            :prop="`headlines.${index}`"
          >
            <div class="flex items-center space-x-2">
              <div class="relative flex-1">
                <el-input
                  v-model="form.headlines[index]"
                  :placeholder="`请输入标题 ${index + 1}`"
                  class="w-full"
                  @input="emitChange"
                  @blur="validateField(`headlines.${index}`)"
                />
                <div class="absolute bottom-1 right-2 text-xs text-gray-400">
                  {{ getByteLength(form.headlines[index] || '') }}/30
                </div>
              </div>
              <span class="text-[#999] text-xs">({{ index + 1 }}/15)</span>
              <el-button
                type="text"
                size="small"
                class="text-gray-400 hover:text-red-500"
                @click="removeHeadline(index)"
                v-show="form.headlines.length > 3"
              >
                <i class="el-icon-delete"></i>
              </el-button>
            </div>
          </el-form-item>
        </div>

        <!-- 推荐词区域 -->
        <div
          class="mt-4 p-4 bg-white rounded-lg border border-gray-200 recommend-area"
          v-if="recommendedHeadlines.length"
        >
          <div class="flex flex-wrap gap-2 pr-20 pb-8">
            <el-tag
              v-for="(word, index) in recommendedHeadlines"
              :key="index"
              class="cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-all duration-200"
              @click="selectRecommendedHeadline(word)"
            >
              {{ word }}
            </el-tag>
          </div>
          <el-button
            type="primary"
            size="mini"
            class="adopt-all-btn"
            @click="adoptAllHeadlines"
          >
            全部采用
          </el-button>
        </div>
      </div>

      <!-- 描述部分 -->
      <div>
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xs font-bold text-gray-900">描述</h3>
          <el-button
            type="primary"
            size="mini"
            @click="addDescription"
            :disabled="form.descriptions.length >= 4"
          >
            <i class="el-icon-plus mr-1"></i>添加
          </el-button>
        </div>

        <div class="space-y-4">
          <el-form-item
            v-for="(description, index) in form.descriptions"
            :key="index"
            :prop="`descriptions.${index}`"
          >
            <div class="flex items-center space-x-2">
              <div class="relative flex-1">
                <el-input
                  v-model="form.descriptions[index]"
                  :placeholder="`请输入描述 ${index + 1}`"
                  class="w-full"
                  @input="emitChange"
                  @blur="validateField(`descriptions.${index}`)"
                />
                <div class="absolute bottom-1 right-2 text-xs text-gray-400">
                  {{ getByteLength(form.descriptions[index] || '') }}/90
                </div>
              </div>
              <span class="text-[#999] text-xs">({{ index + 1 }}/4)</span>
              <el-button
                type="text"
                size="small"
                class="text-gray-400 hover:text-red-500 ml-2"
                @click="removeDescription(index)"
                v-show="form.descriptions.length > 2"
              >
                <i class="el-icon-delete"></i>
              </el-button>
            </div>
          </el-form-item>
        </div>
      </div>
    </el-form>
  </div>
</template>

<script>
import { getByteLength } from '@/views/google/create/components/creatives/index';
export default {
  name: 'AdCreative',
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    keywordList: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const validateHeadline = (rule, value, callback) => {
      const filledHeadlines = this.form.headlines.filter((h) => h && h.trim()).length;

      if (!value || !value.trim()) {
        if (filledHeadlines < 3) {
          callback(new Error('至少需要输入3个标题'));
          return;
        }
        callback();
        return;
      }

      if (getByteLength(value) > 30) {
        callback(new Error('标题长度不能超过30字节'));
        return;
      }
      if (this.isDuplicate(value, this.form.headlines, rule.field)) {
        callback(new Error('标题不能重复'));
        return;
      }
      callback();
    };

    const validateDescription = (rule, value, callback) => {
      const filledDescriptions = this.form.descriptions.filter((d) => d && d.trim()).length;

      if (!value || !value.trim()) {
        if (filledDescriptions < 2) {
          callback(new Error('至少需要输入2个描述'));
          return;
        }
        callback();
        return;
      }

      if (getByteLength(value) > 90) {
        callback(new Error('描述长度不能超过90字节'));
        return;
      }
      if (this.isDuplicate(value, this.form.descriptions, rule.field)) {
        callback(new Error('描述不能重复'));
        return;
      }
      callback();
    };

    return {
      form: {
        headlines: ['', '', ''],
        descriptions: ['', ''],
      },
      rules: {},
      recommendedHeadlines: [],
      validateHeadline,
      validateDescription,
      getByteLength,
    };
  },
  watch: {
    value: {
      handler(newVal, oldVal) {
        // 只在有实质性变化时才重新初始化
        if (!oldVal || 
            (newVal.headlines && newVal.headlines.length !== (oldVal.headlines || []).length) ||
            (newVal.descriptions && newVal.descriptions.length !== (oldVal.descriptions || []).length)) {
          this.initData();
        }
      },
      deep: true,
    },
  },
  created() {
    this.initData();
    this.generateRules();
  },
  methods: {
    initData() {
      // 只在初始化时或外部数据发生实质性变化时才更新
      if (this.value.headlines && this.value.headlines.length > 0) {
        // 保留已有的空输入框，只更新有内容的部分
        const currentFilledCount = this.form.headlines.filter(h => h && h.trim()).length;
        const newFilledCount = this.value.headlines.length;
        
        if (currentFilledCount !== newFilledCount) {
          this.form.headlines = [...this.value.headlines];
          while (this.form.headlines.length < 3) {
            this.form.headlines.push('');
          }
        }
      } else if (this.form.headlines.length === 0) {
        // 初始化时设置默认的3个空输入框
        this.form.headlines = ['', '', ''];
      }

      if (this.value.descriptions && this.value.descriptions.length > 0) {
        const currentFilledCount = this.form.descriptions.filter(d => d && d.trim()).length;
        const newFilledCount = this.value.descriptions.length;
        
        if (currentFilledCount !== newFilledCount) {
          this.form.descriptions = [...this.value.descriptions];
          while (this.form.descriptions.length < 2) {
            this.form.descriptions.push('');
          }
        }
      } else if (this.form.descriptions.length === 0) {
        // 初始化时设置默认的2个空输入框
        this.form.descriptions = ['', ''];
      }
    },

    generateRules() {
      this.rules = {};
      this.form.headlines.forEach((_, index) => {
        this.rules[`headlines.${index}`] = [{ validator: this.validateHeadline, trigger: 'blur' }];
      });
      this.form.descriptions.forEach((_, index) => {
        this.rules[`descriptions.${index}`] = [{ validator: this.validateDescription, trigger: 'blur' }];
      });
    },

    isDuplicate(value, array, currentField) {
      if (!value || !value.trim()) return false;
      const currentIndex = parseInt(currentField.split('.')[1]);
      return array.some((item, index) => index !== currentIndex && item && item.trim() === value.trim());
    },

    loadRecommendedHeadlines() {
      this.recommendedHeadlines = [...this.keywordList];
    },

    addHeadline() {
      if (this.form.headlines.length < 15) {
        this.form.headlines.push('');
        this.generateRules();
        this.emitChange();
      }
    },

    removeHeadline(index) {
      if (this.form.headlines.length > 3) {
        this.form.headlines.splice(index, 1);
        this.generateRules();
        this.emitChange();
      }
    },

    addDescription() {
      if (this.form.descriptions.length < 4) {
        this.form.descriptions.push('');
        this.generateRules();
        this.emitChange();
      }
    },

    removeDescription(index) {
      if (this.form.descriptions.length > 2) {
        this.form.descriptions.splice(index, 1);
        this.generateRules();
        this.emitChange();
      }
    },

    selectRecommendedHeadline(word) {
      if (this.form.headlines.some((h) => h && h.trim() === word.trim())) {
        return;
      }

      const emptyIndex = this.form.headlines.findIndex((h) => !h || !h.trim());

      if (emptyIndex !== -1) {
        this.$set(this.form.headlines, emptyIndex, word);
      } else if (this.form.headlines.length < 15) {
        this.form.headlines.push(word);
        this.generateRules();
      } else {
        return;
      }

      const wordIndex = this.recommendedHeadlines.indexOf(word);
      if (wordIndex > -1) {
        this.recommendedHeadlines.splice(wordIndex, 1);
      }

      this.emitChange();
    },

    adoptAllHeadlines() {
      const emptyIndexes = [];
      this.form.headlines.forEach((h, index) => {
        if (!h || !h.trim()) {
          emptyIndexes.push(index);
        }
      });

      let addedCount = 0;
      const wordsToAdd = this.recommendedHeadlines.filter(
        (word) => !this.form.headlines.some((h) => h && h.trim() === word.trim()),
      );

      wordsToAdd.forEach((word) => {
        if (addedCount < emptyIndexes.length) {
          this.$set(this.form.headlines, emptyIndexes[addedCount], word);
          addedCount++;
        } else if (this.form.headlines.length < 15) {
          this.form.headlines.push(word);
        }
      });

      this.recommendedHeadlines = [];
      this.generateRules();
      this.emitChange();
    },

    emitChange() {
      // 只对外输出有内容的项，但保留内部的空输入框
      const headlines = this.form.headlines.filter((h) => h && h.trim());
      const descriptions = this.form.descriptions.filter((d) => d && d.trim());

      this.$emit('input', {
        ...this.value,
        headlines,
        descriptions,
      });
    },
    // 对外提供的验证方法
    validate() {
      return new Promise((resolve, reject) => {
        this.$refs.form.validate((valid, invalidFields) => {
          if (valid) {
            resolve(true);
          } else {
            reject(
              Object.values(invalidFields)
                .flat()
                .map((field) => field.message),
            );
          }
        });
      });
    },

    // 清除验证错误
    clearValidate() {
      this.$refs.form && this.$refs.form.clearValidate();
    },

    // 验证单个字段
    validateField(prop) {
      this.$refs.form && this.$refs.form.validateField(prop);
    },

    // 清空空输入框
    clearEmptyInputs() {
      // 清空空的标题输入框，保留至少3个
      const filledHeadlines = this.form.headlines.filter(h => h && h.trim());
      this.form.headlines = [...filledHeadlines];
      while (this.form.headlines.length < 3) {
        this.form.headlines.push('');
      }
      
      // 清空空的描述输入框，保留至少2个
      const filledDescriptions = this.form.descriptions.filter(d => d && d.trim());
      this.form.descriptions = [...filledDescriptions];
      while (this.form.descriptions.length < 2) {
        this.form.descriptions.push('');
      }
      
      this.generateRules();
    },
  },
};
</script>

<style scoped lang="scss">
.el-tag {
  margin: 2px;
  transition: all 0.3s ease;
}

.el-tag:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.el-button--text {
  padding: 0;
}

.recommend-area {
  position: relative;
  min-height: 80px;
}

.adopt-all-btn {
  position: absolute;
  bottom: 8px;
  right: 8px;
  z-index: 10;
}
.el-form-item {
  margin-bottom: 0;
}
</style>
